import { useEffect, useMemo, useState } from "react";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { RequirementCard } from "../../components/requirementss/RequirementCard";
import { RequirementFormValues } from "../../components/requirements/RequirementForm";
import { LoginDashboard } from "../../components/manageLoginDashboard/LoginDashboard";
import { DealDeskState, Requirement, Customer, FilterState, RequirementStatus } from "../../types/deals";
import { Customer as CustomerType, CustomerFilterState } from "../../types/customer";
import { Clipboard, BadgeCheck, IndianRupee, TrendingUp } from "lucide-react";
import { RMList } from "../../utils/interfaces";
import apiService from "../../services/apiService";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "../../components/ui/pagination";

const initialFilters: FilterState = {
  search: "",
  products: [],
  statuses: [],
  applicationRMs: [],
  dateRange: {},
  lenderInstitutions: [],
  lenderNames: [],
  requirementTypes: [],
  showStagnant: false,
  customerType: [],
  currentPageFilter: "loggedIn",

};

const initialCustomerFilters: CustomerFilterState = {
  search: "",
  leadRMs: [],
  partnerDeals: [],
  customerTypes: [],
  dateRange: { from: null, to: null },
  products: [],
  showStagnant: false,
};

const initial: DealDeskState = { 
  requirements: [],
  filters: initialFilters
};

interface LenderTypes {
  id: number;
  name: string;
  display_order: number;
  parent_type: string;
}

interface LenderCompanyList {
  id: number;
  name: string;
}

const ManageRequirement = () => {
  const [requirementStatuses, setRequirementStatuses] = useState<RequirementStatus[]>([]);
  const [uniqueRmNames, setUniqueRmNames] = useState<RMList[]>([]);
  const [state, setState] = useState<DealDeskState>(initial);
  const [isFetchingRequirements, setIsFetchingRequirements] = useState(false);
  const [pagination, setPagination] = useState<{ totalRecords: number; totalPages: number; currentPage: number; pageSize: number }>({
    totalRecords: 0,
    totalPages: 1,
    currentPage: 1,
    pageSize: 25,
  });

  // Customer data and filters
  const [customers, setCustomers] = useState<CustomerType[]>([
    // Sample data
    {
      id: "1",
      entityName: "TechCorp Solutions Pvt Ltd",
      customerName: "John Smith",
      designation: "CEO",
      email: "john@techcorp.com",
      phone: "+91-9876543210",
      source: "Direct",
      leadRM: "Rahul Sharma",
      partnerName: "Alpha Partners",
      city: "Mumbai",
      createdAt: new Date().toISOString(),
      createdBy: "Admin",
      customerType: "Customer",
      products: ["Hybrid", "Leasing"],
      status: "Active",
      logo: ""
    },
    {
      id: "2",
      entityName: "Global Manufacturing Ltd",
      customerName: "Sarah Johnson",
      designation: "CFO",
      email: "sarah@globalmanuf.com",
      phone: "+91-9876543211",
      source: "Partner",
      leadRM: "Priya Singh",
      partnerName: "Beta Financial",
      city: "Delhi",
      createdAt: new Date(Date.now() - 86400000).toISOString(),
      createdBy: "Admin",
      customerType: "Prospect",
      products: ["Hybrid"],
      status: "Sanctioned",
      logo: ""
    }
  ]);
  
  const [customerFilters, setCustomerFilters] = useState<CustomerFilterState>(initialCustomerFilters);
  const [formOpen, setFormOpen] = useState(false);
  const [showAdminDashboard, setShowAdminDashboard] = useState(true);
  const [summary, setSummary] = useState<any>();
  const [currentPageFilter, setCurrentPageFilter] = useState<"logged-in" | "to-be-logged-in" | "dropped">("logged-in");
  const [lenderCompanyList, setLenderCompanyList] = useState<LenderCompanyList[]>([]);
  const [lenderUserProductTypes, setLenderUserProductTypes] = useState<LenderTypes[]>([]);
  const [lenderStatus, setLenderStatus] = useState<any>();
  const [loading, setLoading] = useState(true);
  const fetchAll = async (
    page = pagination.currentPage,
    pageSize = pagination.pageSize,
    pageFilter = currentPageFilter,
    filterOverride?: FilterState
  ) => {
    setIsFetchingRequirements(true);
    try {
      const filterPayload = filterOverride ?? state.filters ?? initialFilters;

      const [res, summaryRes] = await Promise.all([
        apiService.post<any>("/login/get-all-requirements", {
          filter: filterPayload,
          pagination: { pageSize, currentPage: page },
          currentPageFilter: pageFilter,
        }),
        apiService.post<any>("/login/requirements-dashboard-count", {
          filter: filterPayload,
          currentPageFilter: pageFilter,
        }),
      ]);

      const maskedRequirements = (res?.Requirements ?? []).map((r: any) => {
        const { customerId, customerName, customerLogo, customerBrandName, ...rest } = r;
        return {
          ...rest,
          customer: {
            id: customerId,
            name: customerName,
            logo: customerLogo,
            brandName: customerBrandName,
          },
        };
      });

      setState((s) => ({
        ...s,
        requirements: maskedRequirements,
        filters: s.filters ?? initialFilters,
      }));

      setSummary((summaryRes as any)?.dashboardCount ?? (summaryRes as any)?.data ?? summaryRes);
      if (res?.pagination) setPagination(res.pagination);
    } catch (e) {
      // keep empty list on error
    } finally {
      setIsFetchingRequirements(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [rmRes, productRes, statusRes] = await Promise.all([
          apiService.get<RMList[]>("/users/rm-list"),
          apiService.get<LenderTypes[]>("/lender/get-lender-user-product-types"),
          apiService.get<any>("/login/get-lender-status"),
        ]);
        fetchLenderCompanyList();
        // setRequirements(reqRes.Requirements);
        setUniqueRmNames(rmRes?.data || []);
        setLenderUserProductTypes(productRes?.data || []);
        setLenderStatus(statusRes?.data || []);
        // setLenderStatus(statusRes.data);

        // saveState({ requirements: reqRes.Requirements, filters }); // optional
      } catch (err) {
        console.error("Error fetching data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const fetchLenderCompanyList = async () => {
    try {
      const response = await apiService.get<{ data: LenderCompanyList[] }>(`/customers/get-lender-company-list`);
      const transformedDocs = response.data.map((doc) => ({
        name: doc.name,
        id: doc.id
      }));
      setLenderCompanyList(transformedDocs);
    } catch (error) {
      console.error("Error fetching lender companies:", error);
    }
  };

  // initial load + when page/pageSize changes
  useEffect(() => {
    fetchAll(pagination.currentPage, pagination.pageSize, currentPageFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.currentPage, pagination.pageSize, currentPageFilter]);

  // useEffect(() => {
  //   const getRMListData = async () => {
  //     try {
  //       const response: { data: { id: number, name: string }[] } = await apiService.get("/users/rm-list");
  //       setUniqueRmNames(response?.data || []);
  //       // if (response.data && response.data.length > 0) {
  //       //   setSelectedRM(response.data[0].id); // Set first RM's ID
  //       // }
  //     } catch (e) {
  //       // handle error
  //     }
  //   };
  //   getRMListData();
  // }, []);

  useEffect(() => {

    async function fetchRequirementStatuses() {
      try {
        const res: any = await apiService.get<any>("/login/requirement-statuses");
       setRequirementStatuses(res?.data || []);
      } catch (e) {
        // keep fallback REQUIREMENT_STATUSES
      }
    }

    fetchRequirementStatuses();
   
  }, []);



  const kpis = useMemo(() => {
    const totalReq = state?.requirements?.length || 0;
    let totalSanctioned = 0;
    let totalDisbursed = 0;
    const payoutList: number[] = [];
    state?.requirements?.forEach((r) => {
      r.lenders.forEach((l) => {
        l.statusHistory.forEach((e) => {
          if (typeof e.sanctionAmount === "number") totalSanctioned += e.sanctionAmount;
          if (typeof e.disbursedAmount === "number") {
            totalDisbursed += e.disbursedAmount;
            if (typeof e.totalRevenue === "number") payoutList.push(e.totalRevenue);
          }
        });
      });
    });
    const averagePayout = payoutList.length
      ? Math.round((payoutList.reduce((a, b) => a + b, 0) / payoutList.length) * 100) / 100
      : 0;
    return { totalReq, totalSanctioned, totalDisbursed, averagePayout };
  }, [state?.requirements]);

  function handleAddRequirement(values: RequirementFormValues) {
    const id = `R-${Math.random().toString(36).slice(2, 8).toUpperCase()}`;
    
    // Default customer for single customer mode
    const defaultCustomer: Customer = {
      id: "default",
      name: "Your Company",
    };
    
    const newReq: Requirement = {
      id,
      type: values.type,
      status: 1, // 1: Due Diligence, 2: Credit Discussion Completed, 3: Under Review, 4: Term Sheet Shared, 5: CC Approved, 6: CI Rejected, 7: CL Rejected, 8: Sanctioned, 9: Disbursed
      products: values.products as any,
      quantum: values.quantum,
      tenure: values.tenure,
      applicationRM: values.applicationRM,
      dealNote: values.dealNote,
      createdAt: new Date().toISOString(),
      createdBy: "You",
      lenders: [],
      customer: defaultCustomer,
    };
    setState((s) => ({ ...s, requirements: [newReq, ...s.requirements] }));
    setFormOpen(false);
  }

  function updateRequirement(updated: Requirement) {
    setState((s) => ({ ...s, requirements: s.requirements.map((r) => r.id === updated.id ? updated : r) }));
  }

  function handleFiltersChange(filters: FilterState) {
    // reset to page 1 when filters change
    setPagination((p) => ({ ...p, currentPage: 1 }));
    setState((s) => ({ ...s, filters }));
    void fetchAll(1, pagination.pageSize, currentPageFilter, filters);
  }

  function handleAddCustomer(customerData: Omit<CustomerType, 'id' | 'createdAt' | 'createdBy'>) {
    const newCustomer: CustomerType = {
      ...customerData,
      id: Math.random().toString(36).substr(2, 9),
      createdAt: new Date().toISOString(),
      createdBy: "Admin"
    };
    setCustomers(prev => [...prev, newCustomer]);
  }

  function handleUpdateCustomer(updated: CustomerType) {
    setCustomers(prev => prev.map(customer => customer.id === updated.id ? updated : customer));
  }

  // Show admin dashboard or single customer view
  if (showAdminDashboard) {
    return (
      <>
        <LoginDashboard
          requirements={state.requirements}
          filters={state.filters ?? initialFilters}
          customers={customers}
          customerFilters={customerFilters}
          onRequirementAdd={handleAddRequirement}
          onRequirementUpdate={updateRequirement}
          onFiltersChange={handleFiltersChange}
          onCustomerAdd={handleAddCustomer}
          onCustomerUpdate={handleUpdateCustomer}
          setCustomerFilters={setCustomerFilters}
          requirementStatuses={requirementStatuses}
          uniqueRmNames={uniqueRmNames}
          summary={summary}
          currentPageFilter={currentPageFilter}
          onCurrentPageFilterChange={(v) => {
            setPagination((p) => ({ ...p, currentPage: 1 }));
            setCurrentPageFilter(v);
          }}
          lender_user_product_types={lenderUserProductTypes}
          LenderStatus={lenderStatus}
          lenderCompanyList={lenderCompanyList}
          
        />

        <div className="w-[100%] px-6 pb-10 bg-white">
          <div className="mt-4 rounded-lg border px-4 py-3 flex flex-col md:flex-row md:items-center gap-3">
            <div className="text-sm text-muted-foreground">
              {pagination.totalRecords === 0 ? (
                "Showing 0 of 0 entries"
              ) : (
                <>
                  Showing {(pagination.currentPage - 1) * pagination.pageSize + 1} to{" "}
                  {Math.min(pagination.currentPage * pagination.pageSize, pagination.totalRecords)} of{" "}
                  {pagination.totalRecords} entries
                </>
              )}
            </div>

            <div className="ml-auto flex items-center gap-2">
              <Pagination>
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious
                      className="border-[#3f8b98] text-[#3f8b98] border rounded-lg h-[34px] w-24"
                      onClick={() =>
                        pagination.currentPage > 1 &&
                        !isFetchingRequirements &&
                        setPagination((p) => ({ ...p, currentPage: p.currentPage - 1 }))
                      }
                    >
                      Previous
                    </PaginationPrevious>
                  </PaginationItem>

                  {Array.from({ length: pagination.totalPages }, (_, index) => (
                    <PaginationItem key={index}>
                      <PaginationLink
                        className={`rounded-lg h-8 w-[30px] ${
                          pagination.currentPage === index + 1
                            ? "bg-[#3f8b98] text-white"
                            : "border-[#3f8b98] text-[#3f8b98] border"
                        }`}
                        onClick={() =>
                          !isFetchingRequirements &&
                          setPagination((p) => ({ ...p, currentPage: index + 1 }))
                        }
                      >
                        {index + 1}
                      </PaginationLink>
                    </PaginationItem>
                  ))}

                  <PaginationItem>
                    <PaginationNext
                      className="border-[#3f8b98] text-[#3f8b98] border rounded-lg h-[34px] w-16"
                      onClick={() =>
                        pagination.currentPage < pagination.totalPages &&
                        !isFetchingRequirements &&
                        setPagination((p) => ({ ...p, currentPage: p.currentPage + 1 }))
                      }
                    >
                      Next
                    </PaginationNext>
                  </PaginationItem>
                </PaginationContent>
              </Pagination>

              <Select
                value={String(pagination.pageSize)}
                onValueChange={(v) => setPagination((p) => ({ ...p, pageSize: Number(v), currentPage: 1 }))}
              >
                <SelectTrigger className="w-[110px]">
                  <SelectValue placeholder="Page size" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="25">25</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <div>
      <header className="py-10">
        <div className="container">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl md:text-4xl font-bold tracking-tight">Manage Funding Requirements & Tag Lenders</h1>
              <p className="mt-2 text-muted-foreground max-w-2xl">
                Capture Leasing and Hybrid requirements, tag multiple lenders, and track statuses, documents, and timelines in one place.
              </p>
            </div>
            <Button variant="outline" onClick={() => setShowAdminDashboard(true)}>
              Switch to Admin Dashboard
            </Button>
          </div>
          <div className="mt-6">
            <Button variant="hero" size="lg" onClick={() => setFormOpen(true)}>Add Requirement</Button>
          </div>
        </div>
      </header>

      <main className="container pb-16">
        <section className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="overflow-hidden">
            <CardContent className="p-4 bg-gradient-to-br from-primary/5 to-transparent">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs text-muted-foreground">Total Requirements</div>
                  <div className="mt-1 text-2xl font-semibold">{kpis.totalReq}</div>
                </div>
                <div className="h-10 w-10 rounded-full bg-primary/10 text-primary flex items-center justify-center">
                  <Clipboard className="h-5 w-5" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="overflow-hidden">
            <CardContent className="p-4 bg-gradient-to-br from-primary/5 to-transparent">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs text-muted-foreground">Total Sanctioned</div>
                  <div className="mt-1 text-2xl font-semibold">{new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(kpis.totalSanctioned)}</div>
                </div>
                <div className="h-10 w-10 rounded-full bg-primary/10 text-primary flex items-center justify-center">
                  <BadgeCheck className="h-5 w-5" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="overflow-hidden">
            <CardContent className="p-4 bg-gradient-to-br from-primary/5 to-transparent">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs text-muted-foreground">Total Disbursed</div>
                  <div className="mt-1 text-2xl font-semibold">{new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(kpis.totalDisbursed)}</div>
                </div>
                <div className="h-10 w-10 rounded-full bg-primary/10 text-primary flex items-center justify-center">
                  <IndianRupee className="h-5 w-5" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="overflow-hidden">
            <CardContent className="p-4 bg-gradient-to-br from-primary/5 to-transparent">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs text-muted-foreground">Average Payout</div>
                  <div className="mt-1 text-2xl font-semibold">{new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(kpis.averagePayout)}</div>
                </div>
                <div className="h-10 w-10 rounded-full bg-primary/10 text-primary flex items-center justify-center">
                  <TrendingUp className="h-5 w-5" />
                </div>
              </div>
            </CardContent>
          </Card>
        </section>
        {state?.requirements?.length === 0 ? (
          <section className="border rounded-xl p-10 text-center bg-card">
            <h2 className="text-xl font-semibold">No requirement created</h2>
            <p className="text-muted-foreground mt-1">Start by adding your first funding requirement.</p>
            <Button className="mt-4" onClick={() => setFormOpen(true)}>Add Requirement</Button>
          </section>
        ) : (
          <section>
            <div className="space-y-4">
              {state?.requirements?.map((r) => (
                <RequirementCard
                  key={r.id}
                  req={r}
                  onUpdate={updateRequirement}
                  requirementStatuses={requirementStatuses}
                />
              ))}
            </div>
          </section>
        )}
      </main>

      {/* <RequirementForm open={formOpen} onOpenChange={setFormOpen} onSubmit={handleAddRequirement} recentQuantum={recentQuantum} /> */}
    </div>
  );
};

export default ManageRequirement;